import { Stack } from "@mui/material";
import { type Location } from "@src/appV2/lib/GoogleMaps";

import { useDistanceToWorkplace } from "../useDistanceToWorkplace";
import { isHomeHealthOrHospiceWorkplace } from "../utils";
import { WorkplaceProfileActions } from "./Actions";
import { WorkplaceProfileHeading } from "./Heading";
import { WorkplaceProfileLocationAndGallery } from "./LocationAndGallery";

export interface WorkplaceProfileOverviewSectionProps {
  type: string;
  name: string;
  workplaceId: string;
  phoneNumber?: string;
  location: Location;
  formattedAddress?: string;
}

export function WorkplaceProfileOverviewSection(props: WorkplaceProfileOverviewSectionProps) {
  const { type, name, workplaceId, phoneNumber, location, formattedAddress } = props;

  const { formattedDistance } = useDistanceToWorkplace({
    workplaceGeoLocation: {
      latitude: location.lat,
      longitude: location.lng,
    },
  });

  const isHomeHealthOrHospice = isHomeHealthOrHospiceWorkplace(type);
  const distanceText =
    isHomeHealthOrHospice || !formattedDistance ? undefined : `${formattedDistance} from home`;

  return (
    <>
      <Stack
        spacing={8}
        paddingBottom={isHomeHealthOrHospice ? 6 : 0}
        data-testid="overview-section-stack"
      >
        <WorkplaceProfileHeading type={type} distanceText={distanceText} />

        <WorkplaceProfileActions
          workplaceId={workplaceId}
          phoneNumber={phoneNumber}
          workplaceType={type}
        />
      </Stack>

      {!isHomeHealthOrHospice && (
        <WorkplaceProfileLocationAndGallery
          workplaceId={workplaceId}
          workplaceName={name}
          workplaceFormattedAddress={formattedAddress}
          location={location}
          scrollViewOuterPaddingCompensation={7}
          data-testid="workplace-location-gallery"
          scrollViewSx={(theme) => ({
            height: "max(13.5rem, 25vh)",
            boxSizing: "content-box",

            // Add padding instead of Stack spacing to ensure box shadows are fully visible
            py: 9,
            // Reduce the total spacing by using negative margin since the padding is larger
            // than needed (to make the shadows fully visible)
            my: `calc(-1 * ${theme.spacing(3)})`,

            // Style all direct children with elevation and rounded corners
            "& > *": {
              boxShadow: theme.shadows[3],
              borderRadius: theme.borderRadius?.medium,
            },
          })}
        />
      )}
    </>
  );
}
